import { useState, useEffect } from "react";
import { useColors } from "../ui";
import { getBlobUrl } from "../blobs";

export function useBlobUrl(sha256: string, mimeType?: string): string | null {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;

    getBlobUrl(sha256, mimeType).then((loadedUrl) => {
      if (cancelled) {
        if (loadedUrl.startsWith("blob:")) URL.revokeObjectURL(loadedUrl);
        return;
      }
      if (loadedUrl.startsWith("blob:")) objectUrl = loadedUrl;
      setUrl(loadedUrl);
    });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [sha256, mimeType]);

  return url;
}

export function BlobImage({
  sha256,
  mimeType,
  style,
  alt,
}: {
  sha256: string;
  mimeType?: string;
  style?: React.CSSProperties;
  alt?: string;
}) {
  const url = useBlobUrl(sha256, mimeType);
  if (!url) return <div style={{ ...style, background: "#333" }} />;
  return <img src={url} style={style} alt={alt || ""} />;
}

export function BlobVideo({
  sha256,
  mimeType,
  style,
}: {
  sha256: string;
  mimeType?: string;
  style?: React.CSSProperties;
}) {
  const url = useBlobUrl(sha256, mimeType);
  if (!url) return <div style={{ ...style, background: "#333" }} />;
  return (
    <video
      src={url}
      controls
      playsInline
      style={{ ...style, objectFit: "cover" }}
    />
  );
}

export function BlobAudio({
  sha256,
  mimeType,
  style,
}: {
  sha256: string;
  mimeType?: string;
  style?: React.CSSProperties;
}) {
  const url = useBlobUrl(sha256, mimeType);
  if (!url) return <div style={{ ...style, background: "#333" }} />;
  return <audio src={url} controls style={style} />;
}

export function BlobFile({
  sha256,
  mimeType,
  style,
}: {
  sha256: string;
  mimeType: string;
  style?: React.CSSProperties;
}) {
  const colors = useColors();
  const url = useBlobUrl(sha256, mimeType);

  const openFile = () => {
    if (url) window.open(url, "_blank");
  };

  const getLabel = () => {
    if (mimeType.startsWith("audio/")) return "Audio";
    if (mimeType.startsWith("video/")) return "Video";
    if (mimeType === "application/pdf") return "PDF";
    if (mimeType.includes("word")) return "Doc";
    if (mimeType.includes("sheet") || mimeType.includes("excel"))
      return "Sheet";
    return mimeType.split("/")[1]?.toUpperCase() || "File";
  };

  const getIcon = () => {
    if (mimeType.startsWith("audio/")) return "\u{1F3B5}";
    if (mimeType.startsWith("video/")) return "\u{1F3AC}";
    if (mimeType === "application/pdf") return "\u{1F4C4}";
    return "\u{1F4CE}";
  };

  return (
    <div
      onClick={openFile}
      style={{
        ...style,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        background: colors.bgAlt,
        border: `1px solid ${colors.border}`,
        cursor: url ? "pointer" : "wait",
        fontSize: "0.65rem",
        color: colors.textMuted,
        textAlign: "center",
        padding: "4px",
        gap: "2px",
      }}
    >
      <span style={{ fontSize: "1.2rem" }}>{getIcon()}</span>
      <span>{getLabel()}</span>
    </div>
  );
}
